<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="800px"
        transition="slide-y-transition"
        eager
        :persistent="actionLoading"
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Zatwierdzenie raportu
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-row>
                    <v-col cols="12" md="auto" class="d-flex">
                        <v-icon class="mr-2" color="success">
                            mdi-timer-outline
                        </v-icon>
                        <span>
                            <strong>Start:</strong>
                            {{ formatDate(report.startDateTime) }}
                        </span>
                    </v-col>
                    <v-col cols="12" md="auto" class="d-flex">
                        <v-icon class="mr-2" color="error">
                            mdi-timer-off-outline
                        </v-icon>
                        <span>
                            <strong>Stop:</strong>
                            {{ formatDate(report.endDateTime) }}
                        </span>
                    </v-col>
                    <v-col cols="12" md="auto" class="d-flex">
                        <v-icon class="mr-2" color="info">
                            mdi-clock-outline
                        </v-icon>
                        <span>
                            <strong>Zaraportowano:</strong>
                            {{ parseDuration(report.duration) }}

                        </span>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <v-col cols="12" md="4">
                        <table class="table table--simple">
                            <tbody>
                                <tr>
                                    <th>Pracownik:</th>
                                    <td>{{ report.user.firstName }} {{ report.user.lastName }}</td>
                                </tr>
                                <tr>
                                    <th>Łączny nakład:</th>
                                    <td>{{ report.reportedAmount }}</td>
                                </tr>
                                <tr>
                                    <th>Suma zadań:</th>
                                    <td>{{ report.finishedReports }}</td>
                                </tr>
                                <tr>
                                    <th>Ilość zaraportowanych zamówień:</th>
                                    <td>{{ report.finishedOrderReportsAmount }}</td>
                                </tr>
                                <tr>
                                    <th>Średnia ilość osób:</th>
                                    <td>{{ report.averageMachineStuffAmount }}</td>
                                </tr>
                                <tr>
                                    <th>Końcowy wynik wykonania planu:</th>
                                    <td>{{ convertToMinutes(report.planExecutionResult) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </v-col>
                    <v-col cols="12" sm="6" md="5" offset-md="3">
                        <canvas ref="chartContainer" />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    :disabled="actionLoading"
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="!stoppedReportingSession"
                    color="success"
                    text
                    :loading="actionLoading"
                    data-sentry-label="Zatwierdzenie raportu"
                    @click="onSave"
                >
                    Zakończ
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'

import { Chart, ArcElement, DoughnutController, Legend } from 'chart.js'
import { sortBy } from 'lodash'
import { appConfig } from '@/config.js'

export default {
    name: 'ReportShiftSummaryDialog',
    mixins: [
        dateFormatter, parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        reportData: {
            type: Object,
            default: null
        },
        actionLoading: {
            type: Boolean
        },
        stoppedReportingSession: {
            type: Boolean
        },
    },
    data() {
        return {
            report: {
                startDateTime: null,
                endDateTime: null,
                duration: null,
                user: {
                    firstName: null,
                    lastName: null,
                }
            },
            chart: null
        }
    },
    computed: {
        hasReports() {
            return this.report.detailedReports.length > 0 && !(this.report.detailedReports.length === 1 &&
            (this.report.detailedReports[0].statusId == 1 || this.report.detailedReports[0].statusId == 90)) // rozpoczęty, przerwany
        },
        diagramColors() {
            return appConfig.reports.diagramColors
        },
        processes() {
            const processesHelper = {}

            const processes = this.report.detailedReports.reduce((processes, item) => {
                const key = `${item.processName}`

                if (!processesHelper[key]) {
                    processesHelper[key] = {
                        processCodeId: item.processCodeId,
                        processName: item.processName,
                        duration: item.duration,
                        diagramColor: item.uiActionColour
                    }

                    processes.push(processesHelper[key])
                } else {
                    processesHelper[key].duration += item.duration
                }

                return processes
            }, [])

            return sortBy(processes, 'processCodeId')
        }
    },
    watch: {
        isOpen(value) {
            if (value) {
                this.report = JSON.parse(JSON.stringify(this.reportData))

                if (this.hasReports) {
                    Chart.register(ArcElement, DoughnutController, Legend)

                    const colors = this.processes.map(process => process.diagramColor)
                    const chartContainer = this.$refs.chartContainer

                    this.chart = new Chart(chartContainer, {
                        type: 'doughnut',
                        data: {
                            labels: this.processes.map(item => `${item.processName} (${this.parseDuration(item.duration)})`),
                            datasets: [{
                                label: 'Zadania',
                                data: this.processes.map(process => process.duration),
                                backgroundColor: colors ? colors : this.diagramColors,
                            }]
                        },
                        options: {
                            plugins: {
                                legend: {
                                    display: true,
                                    position: 'bottom'
                                }
                            },
                        }
                    })
                }
            } else {
                setTimeout(() => {
                    this.report = {
                        startDateTime: null,
                        endDateTime: null,
                        duration: null,
                        user: {
                            firstName: null,
                            lastName: null,
                        }
                    }

                    if (this.chart) {
                        this.chart.destroy()
                    }
                }, 500)
            }
        }
    },
    methods: {
        onSave() {
            this.$emit('on-save', this.report)
        },
        onClose() {
            if (!this.actionLoading) {
                this.$emit('on-close')
            }
        }
    }
}
</script>
